/**
 * LiveClassroomPanel — composes the realistic Video Instructor +
 * Karaoke Mode into a single drop-in panel for the reading pane.
 *
 * Strategy
 *   • The looping mp4 instructor (same Caucasian female from the
 *     brand welcome reel) sits to the LEFT — or stacks on mobile.
 *     She plays muted while TTS speaks; her video freezes the moment
 *     narration stops. Zero user configuration.
 *   • A "Read aloud" / "Stop" toggle plays the current part text via
 *     useSpeechNarration. Karaoke text replaces the static markdown
 *     paragraph while narration is active.
 *   • When the user pauses or finishes, we fall back to the rendered
 *     ReactMarkdown so links / tables / formatting stay intact.
 *
 * Why we strip markdown for narration: SpeechSynthesis doesn't speak
 * "asterisk asterisk bold asterisk asterisk" gracefully. A naive regex
 * pass converts headings, bullets, and emphasis to plain prose
 * suitable for TTS.
 *
 * NOTE: The previous Ready Player Me / 3D talking-head path was
 * removed at the user's explicit request — they want one realistic
 * instructor with no toggles or per-account configuration. See
 * VideoInstructor for the visual.
 */
import { useMemo, useState } from "react";
import { GraduationCap, Headphones, Square, Volume2 } from "lucide-react";
import VideoInstructor from "@/components/VideoInstructor";
import ChalkboardPanel from "@/components/ChalkboardPanel";
import KaraokeText from "@/components/KaraokeText";
import useSpeechNarration from "@/hooks/useSpeechNarration";

function stripMd(md) {
  if (!md) return "";
  return md
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^#+\s*/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/_([^_]+)_/g, "$1")
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    // CRITICAL: preserve paragraph breaks (`\n\n`) but collapse all
    // other runs of whitespace to single spaces. ChalkboardPanel
    // uses double-newlines to detect paragraph boundaries and reveal
    // a chalk-line per paragraph as the TTS narrates. The earlier
    // `\s+ → " "` blanket replace flattened everything into one
    // long line so the chalkboard never found any boundaries.
    .replace(/\n{2,}/g, "\u2424")     // sentinel for paragraph break
    .replace(/[ \t]+/g, " ")
    .replace(/ ?\n ?/g, " ")          // collapse single \n into a space
    .replace(/\u2424/g, "\n\n")       // restore paragraph break
    .trim();
}

export default function LiveClassroomPanel({ markdown, partTitle, lang = "en-US" }) {
  const text = useMemo(() => stripMd(markdown), [markdown]);
  const { speak, stop, speaking, currentWord, wordRange, supported } = useSpeechNarration({ lang });
  const [karaokeMode, setKaraokeMode] = useState(false);
  // Chalkboard mode — replaces the 2D portrait with a blackboard that
  // reveals key phrases paragraph-by-paragraph as the TTS narrates.
  // Persisted in localStorage so the user's choice survives reloads.
  const [chalkboardMode, setChalkboardMode] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.localStorage.getItem("oak.chalkboardMode") === "1"; }
    catch { return false; }
  });
  const onToggleChalkboard = () => {
    setChalkboardMode((prev) => {
      const next = !prev;
      try { window.localStorage.setItem("oak.chalkboardMode", next ? "1" : "0"); }
      catch { /* ignore quota */ }
      return next;
    });
  };

  if (!text) return null;

  const onToggle = () => {
    if (speaking) {
      stop();
      return;
    }
    setKaraokeMode(true);
    speak(text);
  };

  return (
    <div
      className="relative my-6 rounded-2xl border border-white/8 bg-gradient-to-br from-[#0E1420]/70 to-[#0B0E14]/40 p-4 sm:p-5 flex flex-col sm:flex-row gap-4 items-center sm:items-start"
      data-testid="live-classroom-panel"
    >
      <div className="shrink-0">
        {chalkboardMode ? (
          <ChalkboardPanel text={text} speaking={speaking} wordRange={wordRange} size={180} />
        ) : (
          <VideoInstructor speaking={speaking} word={currentWord} size={180} />
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <span className="label-eyebrow !text-[10px] tracking-[0.25em] text-[#00FF9D]/85 flex items-center gap-1.5">
            <Volume2 size={11} /> Live Classroom · Karaoke
          </span>
          {!supported && (
            <span className="text-[10px] text-rose-300/80" data-testid="karaoke-unsupported">
              (browser audio unavailable)
            </span>
          )}
        </div>

        <div className="text-[13px] leading-relaxed font-mono-oak text-white/80 max-h-[140px] overflow-y-auto pr-2 rounded-md">
          {karaokeMode ? (
            <KaraokeText text={text} range={wordRange} testid="karaoke-line" />
          ) : (
            <p className="text-white/55 italic" data-testid="karaoke-prompt">
              {partTitle ? `Tap "Read aloud" to have the instructor narrate "${partTitle}" with synced highlighting.` : "Tap \"Read aloud\" to have the instructor narrate this section with synced highlighting."}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onToggle}
            disabled={!supported}
            className={`text-xs px-3.5 py-2 rounded-full font-display inline-flex items-center gap-1.5 border transition disabled:opacity-40 disabled:cursor-not-allowed ${
              speaking
                ? "border-rose-400/40 bg-rose-400/[0.12] text-rose-200 hover:bg-rose-400/[0.20]"
                : "border-[#00FF9D]/40 bg-[#00FF9D]/[0.10] text-[#9bffcc] hover:bg-[#00FF9D]/[0.18]"
            }`}
            data-testid="btn-karaoke-toggle"
            title={speaking ? "Stop narration" : "Have the instructor read this aloud"}
          >
            {speaking ? (
              <><Square size={11} /> Stop</>
            ) : (
              <><Headphones size={11} /> Read aloud</>
            )}
          </button>
          <span className="text-[10px] text-white/35">
            {Math.max(1, Math.round(text.split(/\s+/).length / 180))} min · ~{text.split(/\s+/).length.toLocaleString()} words
          </span>
          <button
            onClick={onToggleChalkboard}
            className={`ml-auto text-[10px] px-2.5 py-1.5 rounded-full font-mono-oak inline-flex items-center gap-1 border transition tracking-[0.12em] uppercase ${
              chalkboardMode
                ? "border-emerald-400/45 bg-emerald-500/[0.12] text-emerald-200"
                : "border-white/15 bg-white/[0.04] text-white/60 hover:bg-white/[0.08] hover:text-white/85"
            }`}
            data-testid="btn-toggle-chalkboard"
            aria-pressed={chalkboardMode}
            title={chalkboardMode ? "Switch back to the instructor portrait" : "Switch to chalkboard mode — see key ideas drawn as I speak"}
          >
            <GraduationCap size={10} /> {chalkboardMode ? "Chalkboard · On" : "Chalkboard"}
          </button>
        </div>
      </div>
    </div>
  );
}
